import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import ConfigContext from '../../../contexts/ConfigContext';
import useAuth from '../../../hooks/useAuth';
import { useUserLogoutMutation } from '../../../redux/slices/authSlice';

// Components
import MobileSubMenu from './MobileSubMenu';
import FoodTrucksMenu from './FoodTrucksMenu';
import CateringMenu from './CateringMenu';

import styles from './Header.module.postcss';

// Label + optional chevron row (buttons and links share this layout)
function NavRow({ label, chevron = true, className }) {
  return (
    <span className={cn(styles.MobileMenu_LinkInner, className)}>
      <span className={styles.MobileMenu_LinkLabel}>{label}</span>
      {chevron && <span className={styles.MobileMenu_LinkChevron}>{'›'}</span>}
    </span>
  );
}

function MobileMenu({ cityName, vehicleType, mobileMenuOpen }) {
  const { auth, admin, cateringUser, dashboardLink } = useAuth();
  const { facebook, instagram, tiktok } = useContext(ConfigContext);
  // Every city runs the same vendors landing page now; Portland used to be
  // sent straight to /vendor/register when it was still the legacy site.
  const addTruckLink = '/vendors';

  const [foodTrucksSubNavOpen, setFoodTrucksSubNavOpen] = useState(false);
  const [cateringSubNavOpen, setCateringSubNavOpen] = useState(false);
  const [servicesSubNavOpen, setServicesSubNavOpen] = useState(false);

  const [userLogout] = useUserLogoutMutation();

  const closeSubNavs = () => {
    setFoodTrucksSubNavOpen(false);
    setCateringSubNavOpen(false);
    setServicesSubNavOpen(false);
  };

  useEffect(() => {
    if (!mobileMenuOpen) closeSubNavs();
  }, [mobileMenuOpen]);

  return (
    <>
      <nav
        id='mobileMenu'
        className={cn(styles.MobileMenu, {
          [styles.MobileMenu_open]: mobileMenuOpen,
        })}
      >

        {/* Book Catering banner CTA */}
        {!auth.loggedIn && (
          <Link to='/catering/requests/new' className={styles.MobileMenu_CateCta}>
            <div className={styles.MobileMenu_CateCtaContent}>
              <strong>Bring a food {vehicleType.toLowerCase()} to your next event</strong>
              <div className={styles.MobileMenu_CateCtaAccent} />
              <span className={styles.MobileMenu_CateCtaSubtitle}>
                Tell us what you&apos;re planning. We&apos;ll find {vehicleType.toLowerCase()}s that fit.
              </span>
              <div className={styles.MobileMenu_CateCtaBtn}>Get catering quotes →</div>
            </div>
          </Link>
        )}

        {/* Food Trucks — opens submenu */}
        <button
          type='button'
          className={styles.MobileMenu_Link}
          onClick={() => setFoodTrucksSubNavOpen(true)}
          aria-expanded={foodTrucksSubNavOpen}
          aria-controls='foodTrucksSubNav'
        >
          <NavRow label={`Food ${vehicleType}s`} />
        </button>

        {/* Catering — opens submenu */}
        <button
          type='button'
          className={styles.MobileMenu_Link}
          onClick={() => setCateringSubNavOpen(true)}
          aria-expanded={cateringSubNavOpen}
          aria-controls='cateringSubNav'
        >
          <NavRow label='Catering' />
        </button>

        {/* Services — opens submenu */}
        <button
          type='button'
          className={styles.MobileMenu_Link}
          onClick={() => setServicesSubNavOpen(true)}
          aria-expanded={servicesSubNavOpen}
          aria-controls='servicesSubNav'
        >
          <NavRow label='Services' />
        </button>

        {/* Add Your Truck (logged out) */}
        {!auth.loggedIn && (
          <Link to={addTruckLink} className={styles.MobileMenu_Link}>
            <NavRow label={`Add your ${vehicleType.toLowerCase()}`} />
          </Link>
        )}

        {/* Admin link */}
        {admin && (
          <Link to='/admin' className={styles.MobileMenu_Link}>
            <NavRow label='Admin' />
          </Link>
        )}

        {/* Dashboard (logged in, non-admin) */}
        {auth.loggedIn && !admin && (
          <Link to={dashboardLink} className={styles.MobileMenu_Link}>
            <NavRow label='Dashboard' />
          </Link>
        )}

        {/* Account Settings (catering users) */}
        {cateringUser && (
          <Link to='/account' className={styles.MobileMenu_Link}>
            <NavRow label='Account settings' />
          </Link>
        )}

        {/* Support */}
        <Link
          to={cateringUser ? '/catering/contact' : '/support'}
          className={styles.MobileMenu_Link}
        >
          <NavRow label='Support' />
        </Link>

        {/* Log In (logged out) */}
        {!auth.loggedIn && (
          <Link to='/login' className={styles.MobileMenu_Link}>
            <NavRow label='Log in' chevron={false} />
          </Link>
        )}

        {/* Log Out */}
        {auth.loggedIn && (
          <button
            type='button'
            onClick={userLogout}
            className={styles.MobileMenu_Link}
          >
            <NavRow label='Log out' chevron={false} />
          </button>
        )}

        {/* Find Trucks CTA */}
        <Link to='/explore' className={styles.MobileMenu_NearMe}>
          Find food {vehicleType.toLowerCase()}s
        </Link>

        {/* Social footer */}
        <div className={styles.MobileMenu_SocialFooter}>
          <span className={styles.MobileMenu_SupportLocal}>
            Support {cityName}&apos;s local food {vehicleType.toLowerCase()} community
          </span>
          <div className={styles.MobileMenu_SocialIcons}>
            {instagram && (
              <a href={`https://www.instagram.com/${instagram}`} target='_blank' rel='noreferrer'>
                <img src='/static/images/icons/footer/instagram.svg' alt='Instagram' />
              </a>
            )}
            {facebook && (
              <a href={facebook} target='_blank' rel='noreferrer'>
                <img src='/static/images/icons/footer/facebook.svg' alt='Facebook' />
              </a>
            )}
            {tiktok && (
              <a href={`https://www.tiktok.com/@${tiktok}`} target='_blank' rel='noreferrer'>
                <img src='/static/images/icons/footer/tiktok.svg' alt='TikTok' />
              </a>
            )}
          </div>
        </div>

      </nav>

      <MobileSubMenu
        id='foodTrucksSubNav'
        title={`Food ${vehicleType}s`}
        active={foodTrucksSubNavOpen && mobileMenuOpen}
        onClose={closeSubNavs}
      >
        <FoodTrucksMenu cityName={cityName} vehicleType={vehicleType} />
      </MobileSubMenu>

      <MobileSubMenu
        id='cateringSubNav'
        title='Catering'
        active={cateringSubNavOpen && mobileMenuOpen}
        onClose={closeSubNavs}
      >
        <CateringMenu vehicleType={vehicleType} />
      </MobileSubMenu>

      <MobileSubMenu
        id='servicesSubNav'
        title='Services'
        active={servicesSubNavOpen && mobileMenuOpen}
        onClose={closeSubNavs}
      >
        <Link to='/services/office-parks' className={styles.MobileMenu_Link}>
          <NavRow label='Office parks' chevron={false} />
        </Link>
        <Link to='/services/residential' className={styles.MobileMenu_Link}>
          <NavRow label='Residential' chevron={false} />
        </Link>
        <Link to='/services/breweries' className={styles.MobileMenu_Link}>
          <NavRow label='Breweries' chevron={false} />
        </Link>
      </MobileSubMenu>
    </>
  );
}

export default MobileMenu;
